import React from "react";
import styled from "styled-components";
import { AssignNewLessonButton } from "./AssignNewLessonButton";
import { CreateGroupsMenuButton } from "./CreateGroupsMenuButton";
import { CreateGroupsTip, CreateGroupsTipCopy, DEFAULT_LESSON_GROUPS_DETAIL } from "./createGroupsTip";

// "Availability + Create Groups" row (Figma 4277-12534): group-availability
// status on the left; "Create Groups:" + info tooltip, a primary "Lesson
// Groups" and a secondary "Quick Group" action on the right. Reusable so any
// Groups view can offer the full create flow.
const Row = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const StatusText = styled.div`
  color: #002e19;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  margin-left: auto;
`;

const Label = styled.div`
  align-items: center;
  color: #002e19;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 4px;
  line-height: 20px;
  white-space: nowrap;
`;

const PrimaryButton = styled.button`
  align-items: center;
  background: #605dec;
  border: 1px solid #605dec;
  border-radius: 8px;
  color: #faf7f2;
  cursor: pointer;
  display: inline-flex;
  font-size: 18px;
  font-weight: 700;
  gap: 6px;
  height: 48px;
  letter-spacing: 0.25px;
  padding: 0 16px;
  white-space: nowrap;
  &:hover:not(:disabled) { background: #524fc9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const InfoButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  padding: 0;
`;

const InfoWrap = styled.span`
  align-items: center;
  display: inline-flex;
  position: relative;

  ${CreateGroupsTip} {
    display: none;
  }
  &:hover ${CreateGroupsTip},
  &:focus-within ${CreateGroupsTip} {
    display: block;
  }
`;

const InfoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#002e19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const PlusCircleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#faf7f2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const CreateGroupsInfo = ({ lessonGroupsDetail }) => (
  <InfoWrap>
    <InfoButton type="button" aria-label="About creating groups">
      <InfoIcon />
    </InfoButton>
    <CreateGroupsTip role="tooltip">
      <CreateGroupsTipCopy lessonGroupsDetail={lessonGroupsDetail} />
    </CreateGroupsTip>
  </InfoWrap>
);

const TodaysCreateControls = ({ onCreateLessonGroups, onCreateQuickGroup, lessonGroupsDetail }) => (
  <Actions>
    <Label>
      Create Groups:
      <CreateGroupsInfo lessonGroupsDetail={lessonGroupsDetail} />
    </Label>
    <PrimaryButton type="button" onClick={onCreateLessonGroups}>
      <PlusCircleIcon />
      Lesson Groups
    </PrimaryButton>
    <AssignNewLessonButton onClick={onCreateQuickGroup}>Quick Group</AssignNewLessonButton>
  </Actions>
);

const CreateControls = ({
  createControls,
  menuResetKey,
  onOpenCreateMenu,
  onCreateWarmUpGroups,
  onCreateLessonGroups,
  onCreateQuickGroup,
  lessonGroupsDetail,
  focusMenuButton,
  onMenuButtonFocused,
}) => {
  if (createControls === "none") return null;
  if (createControls === "menu") {
    return (
      <Actions>
        <CreateGroupsMenuButton
          key={menuResetKey}
          onOpen={onOpenCreateMenu}
          onCreateLessonGroups={onCreateLessonGroups}
          onCreateQuickGroup={onCreateQuickGroup}
          onCreateWarmUpGroups={onCreateWarmUpGroups}
          focusRequest={focusMenuButton}
          onFocusRequestDone={onMenuButtonFocused}
        />
      </Actions>
    );
  }
  return (
    <TodaysCreateControls
      onCreateLessonGroups={onCreateLessonGroups}
      onCreateQuickGroup={onCreateQuickGroup}
      lessonGroupsDetail={lessonGroupsDetail}
    />
  );
};

// createControls: "buttons" (today's label, tooltip and two buttons), "menu"
// (the single Create Groups menu, warm_up_challenges on) or "none" (the flag
// is not known yet, so neither is shown).
export const CreateGroupsBar = ({
  available,
  total,
  onCreateLessonGroups,
  onCreateQuickGroup,
  // Tail of the Lesson Groups sentence — the standards flow describes what
  // groups align to differently than the lesson flow.
  lessonGroupsDetail = DEFAULT_LESSON_GROUPS_DETAIL,
  createControls = "buttons",
  onOpenCreateMenu,
  onCreateWarmUpGroups,
  // The menu starts closed again whenever this changes (the selected class).
  menuResetKey,
  focusMenuButton = false,
  onMenuButtonFocused,
}) => (
  <Row>
    {typeof total === "number" && (
      <StatusText>{available} of {total} Groups Available</StatusText>
    )}
    <CreateControls
      createControls={createControls}
      menuResetKey={menuResetKey}
      onOpenCreateMenu={onOpenCreateMenu}
      onCreateWarmUpGroups={onCreateWarmUpGroups}
      onCreateLessonGroups={onCreateLessonGroups}
      onCreateQuickGroup={onCreateQuickGroup}
      lessonGroupsDetail={lessonGroupsDetail}
      focusMenuButton={focusMenuButton}
      onMenuButtonFocused={onMenuButtonFocused}
    />
  </Row>
);
